import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ContractInsightsSection } from './ContractInsightsSection';
import { allocatedCostByContract } from './contractStaffing';
import { usePlanEditor } from './editor';

import { useBudgetModel } from '@/analytics';
import { formatFactorAs } from '@/components/financials/metricReading';
import {
  FigureTooltip,
  MICRO_LABEL_TRACKING,
  SectionHeading,
  SidePanelHeading,
} from '@/components/ui';
import { formatAmount, formatHours } from '@/lib';

const Tile = ({
  label,
  value,
  sub,
  help,
  automationId,
  subAutomationId,
}: {
  label: string;
  value: string;
  sub: string;
  /** What the figure means, shown on hover and focus (`FigureTooltip`). */
  help: string;
  automationId?: string;
  subAutomationId?: string;
}) => (
  <FigureTooltip title={help}>
    <Box
      tabIndex={0}
      sx={{
        flex: 1,
        bgcolor: 'var(--bg-sunken)',
        borderRadius: 'var(--border-radius-medium)',
        px: 1.5,
        py: 1,
      }}
    >
      <Box
        sx={{
          fontSize: 10,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: MICRO_LABEL_TRACKING,
          mb: 0.5,
        }}
      >
        {label}
      </Box>
      <Box
        data-automation-id={automationId}
        sx={{
          fontFamily: 'var(--font-family-mono)',
          fontSize: 16,
          fontWeight: 500,
          color: 'var(--fg-1)',
          lineHeight: 1,
        }}
      >
        {value}
      </Box>
      <Box
        data-automation-id={subAutomationId}
        sx={{ fontFamily: 'var(--font-family-mono)', fontSize: 10, color: 'var(--fg-3)', mt: 0.5 }}
      >
        {sub}
      </Box>
    </Box>
  </FigureTooltip>
);

/**
 * The v8 no-contract line (Area 5): absent is never rendered as 0 — with no
 * contract there is no budget figure at all, and planning stands on its own.
 */
export const NoContractNote = ({ variant = 'default' }: { variant?: 'default' | 'internal' }) => {
  const { t } = useTranslation('pmPlanningPanel');
  return (
    <Box
      data-automation-id="no-contract-note"
      sx={{ fontSize: 11, color: 'var(--fg-3)', lineHeight: 1.5 }}
    >
      {variant === 'internal'
        ? t('planBudget.NoContractInternal')
        : t('planBudget.NoContractDefault')}
    </Box>
  );
};

/**
 * Hours on tasks that name no contract (D6/D7): they count toward the plan and
 * price at nothing, and the user has to be able to see which part of the plan
 * that is — `unassignedHours` has reached the API since day one and was never
 * shown. Renders nothing when every leaf is mapped.
 */
export const UnmappedHoursNote = () => {
  const { rollup } = usePlanEditor();
  const { t } = useTranslation('pmPlanningPanel');
  if (rollup.unassignedHours <= 0) return null;
  return (
    /*
      A chip with its own ground, not coloured text. It read as
      `--color-warning-80` on the panel's grey, and that token is a FILL —
      ~1.9:1 on white by tokens.css's own account — so the note was barely
      legible exactly where it mattered. The pale warning ground carries the
      same meaning and lets the text be `--fg-warning-text` (~4.6:1), which is
      what every other warning in this feature already uses.
    */
    <Box
      data-automation-id="unmapped-hours-note"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        px: 1,
        py: 0.25,
        borderRadius: 'var(--border-radius-pill)',
        bgcolor: 'var(--color-warning-10)',
        color: 'var(--fg-warning-text)',
        fontSize: 11,
        lineHeight: 1.5,
      }}
    >
      {t('planBudget.UnmappedHours', { hours: formatHours(rollup.unassignedHours) })}
    </Box>
  );
};

/**
 * The plan-vs-budget numbers both panel modes read (CALC-15). `budget` is null
 * when the project has no contracts — the v8 empty state, not a zero (Area 5).
 */
const usePlanBudgetTotals = () => {
  const { rollup, consumption, contractInputs, state } = usePlanEditor();

  const planned = rollup.totals.hours;
  const noteVariant = 'default' as const;
  if (contractInputs.length === 0) {
    return { planned, totals: rollup.totals, budget: null, noteVariant };
  }

  const hours = consumption.reduce((sum, contract) => sum + contract.hoursBudget, 0);
  // v8: contract revenue incl. T&M extras (CALC-15) — the whole contract line.
  const revenue = consumption.reduce((sum, contract) => sum + contract.revenueBudget, 0);
  // The Own Work amounts across the contracts: what the plan's budgeted revenue
  // is measured against (2026-09-24, the pilot's ask). A task prices its OWN
  // hours, so the whole contract line — expenses, sub-contractors and
  // materials included — overstated the denominator.
  const ownRevenue = consumption.reduce((sum, contract) => sum + contract.ownRevenueBudget, 0);
  const remaining = hours - planned;
  const over = remaining < 0;
  const barPct = hours > 0 ? Math.min(100, (planned / hours) * 100) : 0;
  // The plan's allocated hours (CALC-22): each root's rollup already sums its
  // leaves, so the roots sum to the plan. Over the hours budget it is the
  // "N% allocated" figure (2026-09-28, the pilot's ask — it used to be the
  // DISTRIBUTED share, which the bar and the remaining line already say).
  const allocatedHours = state.document.plan.reduce(
    (sum, root) => sum + (rollup.nodes[root.id]?.allocatedHours ?? 0),
    0,
  );
  const allocatedPct = hours > 0 ? (allocatedHours * 100) / hours : 0;
  return {
    planned,
    totals: rollup.totals,
    budget: { hours, revenue, ownRevenue, remaining, over, barPct, allocatedHours, allocatedPct },
    noteVariant,
  };
};

/**
 * The plan-vs-budget section — the rail's first section in BOTH modes since
 * 2026-09-25 (the pilot's ask): the planned hours against the contracts' Own
 * Work hours, the bar, the remaining line and the two tiles. Its title,
 * "Project summary", is on the drawer's collapse row (`PlanDrawerHeading`).
 * With a task selected it sits above the task insight, where a condensed
 * "Project total" band used to — one section, one set of numbers, no second
 * layout to keep in step.
 */
export const PlanBudgetSection = () => {
  const { rollup, state, resolveCost } = usePlanEditor();
  const { planned, budget, noteVariant } = usePlanBudgetTotals();
  const metric = useBudgetModel().config.metric;
  const { t } = useTranslation(['pmPlanningPanel', 'pmCommon']);

  /*
    The allocated uplift (2026-09-25, the pilot's ask, in place of the realized
    tile): the contracts' Own Work amounts — what the customer pays for the
    firm's own hours — over the plan's allocated cost (CALC-21, the per-contract
    buckets summed), shown in the configured metric like every other
    factor-shaped number (ADR 0027). The first cut divided the allocated
    REVENUE by the cost, which the pilot corrected the same day: the question
    is what the staffing costs against what was sold, not what the staffing
    would bill. An unmapped task consumes no contract, so its cost is not in
    the denominator. With no allocated cost there is no ratio, and the
    sub-line says so rather than dividing by zero.
  */
  // The staffing LINES' cost only (2026-09-29, the pilot's ask) — the
  // consumption's cost prices an unallocated task by its budget, which is not
  // what the staffing costs. No allocation anywhere: no cost, a dash.
  const allocatedCost = Array.from(
    allocatedCostByContract(state.document.plan, resolveCost).values(),
  ).reduce((sum, cost) => sum + cost, 0);
  const allocatedFactor = budget && allocatedCost > 0 ? budget.ownRevenue / allocatedCost : null;
  const allocatedFactorSub = !budget
    ? t('shared.NoContractYet')
    : allocatedCost > 0
      ? t('shared.OwnWorkAndCost', {
          ownWork: formatAmount(budget.ownRevenue),
          cost: formatAmount(allocatedCost),
        })
      : t('shared.NoAllocationsYet');

  return (
    <Box
      data-automation-id="plan-budget-section"
      // Under the panel's title since 3 Nov; before, the rail started here and
      // pulled up one unit towards the chevron row (2026-09-25, the pilot's ask).
      sx={{
        pt: 0,
        pb: 6,
        borderBottom: 'var(--border-width-thin) solid var(--border-divider)',
      }}
    >
      {/* Hours, headed as the task panel's first section (3 Nov). */}
      <Box sx={{ mb: 2 }}>
        <SectionHeading icon="schedule">{t('pmCommon:term.Hours')}</SectionHeading>
      </Box>
      {budget ? (
        <Box sx={{ mb: 1 }}>
          <UnmappedHoursNote />
        </Box>
      ) : null}
      <FigureTooltip title={budget ? t('help.PlannedHours') : t('help.PlannedHoursNoBudget')}>
        <Box
          tabIndex={0}
          data-automation-id="side-hours-line"
          sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}
        >
          <Box
            component="span"
            data-automation-id="side-planned-hours"
            sx={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 32,
              fontWeight: 500,
              color: 'var(--fg-1)',
              lineHeight: 1,
            }}
          >
            {formatHours(planned)}
          </Box>
          <Box component="span" sx={{ fontSize: 14, color: 'var(--fg-3)' }}>
            {budget
              ? t('planBudget.HoursDistributedOf', { hours: formatHours(budget.hours) })
              : t('planBudget.HoursPlanned')}
          </Box>
          {/*
          The allocation percentage, right-aligned on the hours line above the
          bar (2026-09-25, the pilot's ask — its third home: the title row,
          then the "remaining" line under the bar, now here).
        */}
          {budget ? (
            <Box
              component="span"
              data-automation-id="side-allocated"
              sx={{
                marginLeft: 'auto',
                fontSize: 11,
                color: budget.allocatedPct > 100 ? 'var(--color-error-80)' : 'var(--fg-3)',
              }}
            >
              {t('planBudget.PctAllocated', { pct: Math.round(budget.allocatedPct) })}
            </Box>
          ) : null}
        </Box>
      </FigureTooltip>
      {budget ? (
        <>
          <Box
            data-automation-id="side-budget-bar"
            sx={{
              height: 6,
              bgcolor: 'var(--bg-sunken)',
              borderRadius: 'var(--border-radius-pill)',
              overflow: 'hidden',
              mb: 1,
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${budget.barPct}%`,
                bgcolor: budget.over ? 'var(--color-error-80)' : 'var(--color-pink-80)',
                transition: 'width var(--duration-slow) var(--easing-standard)',
              }}
            />
          </Box>
          <Box sx={{ fontSize: 11, color: 'var(--fg-3)' }} data-automation-id="side-remaining">
            {budget.over
              ? t('planBudget.HoursOverBudget', { hours: formatHours(Math.abs(budget.remaining)) })
              : t('shared.HoursRemaining', { hours: formatHours(budget.remaining) })}
          </Box>
        </>
      ) : (
        <NoContractNote variant={noteVariant} />
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        {/* Forecasted factor first, then the revenue it is read from (2026-11-02,
            the designer's ask) — the same order as the task panel's KPI boxes. */}
        <Tile
          label={t(
            metric === 'factor'
              ? 'pmCommon:term.ForecastedFactor'
              : 'pmCommon:term.ForecastedMargin',
          )}
          value={formatFactorAs(metric, allocatedFactor)}
          sub={allocatedFactorSub}
          help={metric === 'factor' ? t('help.ForecastFactor') : t('help.ForecastMargin')}
          automationId="side-allocated-factor"
          subAutomationId="side-allocated-factor-sub"
        />
        <Tile
          label={t('pmCommon:term.ForecastedRevenue')}
          value={budget ? formatAmount(rollup.totals.amount) : '—'}
          sub={
            budget
              ? t('planBudget.OfOwnWorkBudget', { amount: formatAmount(budget.ownRevenue) })
              : ''
          }
          help={t('help.BudgetedRevenue')}
          automationId="side-planned-revenue"
          subAutomationId="side-planned-revenue-of"
        />
      </Box>
    </Box>
  );
};

/**
 * Default side-panel mode: the whole plan against the contract budgets
 * (CALC-15). Titled as the task view is, with the Project Portfolio's
 * `SidePanelHeading` (3 Nov, Mette) — "Project summary" rather than the
 * project's name, which is the page's title right beside it.
 */
export const PlanVsBudgetPanel = () => {
  const { t } = useTranslation('pmCommon');
  return (
    <>
      <Box sx={{ pt: 2, mb: 2 }} data-automation-id="plan-summary-heading">
        <SidePanelHeading name={t('term.ProjectSummary')} />
      </Box>
      <PlanBudgetSection />
      <ContractInsightsSection />
    </>
  );
};
